import React, { useState, useEffect, useMemo } from 'react';
import DatePickerField from '../../../components/UI/DatePickerField';
import {
  FileText,
  Download,
  Search,
  X,
  Calendar,
  Users,
  Clock,
  DollarSign,
  TrendingUp,
  Target,
  GripVertical,
  AlertTriangle
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from '../../../utils/toast';

import {
  exportToCSV,
  exportToExcel,
  exportToPDF,
  getAvailableFields,
  getAvailableFieldsAsync,
  generateReportSmart,
} from '../../../utils/reportUtils';
import { toLocalISODate, parseLocalDate, getTodayInSingaporeTimezone } from '../../../utils/dateUtils';

// Sortable Field Component
const SortableField = ({ field, isSelected, onToggle, showDragHandle = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg border border-transparent hover:border-gray-200 transition-colors ${
        isSelected ? 'bg-red-50 border-red-200' : ''
      }`}
    >
      {showDragHandle && (
        <div {...attributes} {...listeners} className="cursor-grab">
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>
      )}
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggle(field.id)}
        className="rounded border-gray-300 text-red-600 focus-visible:ring-red-500"
      />
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900">{field.label}</div>
      </div>
      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
        {field.type}
      </span>
    </div>
  );
};

// Available Fields Panel Component
const AvailableFieldsPanel = ({ availableFields, selectedFields, onFieldToggle, searchTerm, onSearchChange }) => {
  const filteredFields = useMemo(() => {
    if (!searchTerm.trim()) {
      return availableFields;
    }
    return availableFields.filter(field =>
      field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableFields, searchTerm]);

  const handleSelectAll = () => {
    const allFieldIds = availableFields.map(f => f.id);
    const newSelection = selectedFields.length === availableFields.length ? [] : allFieldIds;
    // Update all fields at once
    newSelection.forEach(id => {
      if (!selectedFields.includes(id)) onFieldToggle(id);
    });
    selectedFields.forEach(id => {
      if (!newSelection.includes(id)) onFieldToggle(id);
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6 flex flex-col h-full">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-base font-semibold text-gray-900 truncate min-w-0">Available Fields</h3>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <span className="hidden sm:inline text-xs text-gray-500">
            {filteredFields.length} of {availableFields.length} fields
          </span>
          <span className="sm:hidden text-xs text-gray-500">
            {filteredFields.length}/{availableFields.length}
          </span>
          {availableFields.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium px-2 py-1 rounded hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              {selectedFields.length === availableFields.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>
      </div>

      {/* Search Input */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input-admin w-full pl-10 pr-4"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Fields List */}
      <div className="space-y-2 flex-1 overflow-y-auto">
        {filteredFields.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'No fields match your search' : 'No fields available'}
          </div>
        ) : (
          filteredFields.map((field) => (
            <SortableField
              key={field.id}
              field={field}
              isSelected={selectedFields.includes(field.id)}
              onToggle={onFieldToggle}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Selected Fields Panel Component
const SelectedFieldsPanel = ({ availableFields, selectedFields, onFieldToggle, onReorderFields }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const selectedFieldObjects = selectedFields
    .map(fieldId => availableFields.find(f => f.id === fieldId) || { id: fieldId, label: fieldId, type: 'string' });

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedFields.indexOf(active.id);
      const newIndex = selectedFields.indexOf(over.id);

      const newSelectedFields = arrayMove(selectedFields, oldIndex, newIndex);
      onReorderFields(newSelectedFields);
    }
  };

  const handleRemoveField = (fieldId) => {
    onFieldToggle(fieldId); // This will remove it from selectedFields
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6 flex flex-col h-full">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-base font-semibold text-gray-900 truncate min-w-0">Selected Fields</h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {selectedFields.length} <span className="hidden sm:inline">field{selectedFields.length !== 1 ? 's' : ''} selected</span>
          </span>
          {selectedFields.length > 0 && (
            <button
              onClick={() => selectedFields.forEach(id => onFieldToggle(id))}
              className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Selected Fields List */}
      <div className="flex-1 overflow-y-auto">
        {selectedFieldObjects.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-base font-medium mb-2">No fields selected</p>
              <p className="text-xs">Drag fields from the left panel or click to select them</p>
            </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedFields}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {selectedFieldObjects.map((field) => (
                  <div key={field.id} className="relative group">
                    <SortableField
                      field={field}
                      isSelected={true}
                      onToggle={() => handleRemoveField(field.id)}
                      showDragHandle={true}
                    />
                    <button
                      onClick={() => handleRemoveField(field.id)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      title="Remove field"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Field Order Info */}
      {selectedFieldObjects.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-800">
            💡 <strong>Tip:</strong> Drag the grip handle (⋮⋮) to reorder fields in your report
          </p>
        </div>
      )}

    </div>
  );
};

const AdhocReports = () => {
  const [queryBuilder, setQueryBuilder] = useState({
    dataSource: '',
    selectedFields: [],
    dateRange: 'this_month',
    startDate: '',
    endDate: '',
    department: '',
    status: ''
  });

  // Persist a single global selection of fields across sources
  useEffect(() => {
    try {
      const stored = localStorage.getItem('reportFields_GLOBAL');
      if (stored && (!queryBuilder.selectedFields || queryBuilder.selectedFields.length === 0)) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setQueryBuilder(prev => ({ ...prev, selectedFields: parsed }));
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      if (queryBuilder.selectedFields && queryBuilder.selectedFields.length > 0) {
        localStorage.setItem('reportFields_GLOBAL', JSON.stringify(queryBuilder.selectedFields));
      }
    } catch {}
  }, [queryBuilder.selectedFields]);
  const [reportData, setReportData] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [fieldSearchTerm, setFieldSearchTerm] = useState('');

  // Available data sources
  const dataSources = [
    {
      id: 'employees',
      label: 'Employee Data',
      icon: Users,
      description: 'Staff information, demographics, employment details'
    },
    {
      id: 'attendance',
      label: 'Attendance Records',
      icon: Clock,
      description: 'Clock in/out times, attendance patterns'
    },
    {
      id: 'leave',
      label: 'Leave Applications',
      icon: Calendar,
      description: 'Leave requests, approvals, balances'
    },
    {
      id: 'payroll',
      label: 'Payroll Data',
      icon: DollarSign,
      description: 'Salary, allowances, deductions'
    },
    {
      id: 'training',
      label: 'Training & Certifications',
      icon: TrendingUp,
      description: 'Training records, certifications'
    }
  ];

  // Get available fields for selected data source
  const [availableFields, setAvailableFields] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!queryBuilder.dataSource) {
        if (mounted) setAvailableFields([]);
        return;
      }

      try {
        const fields = await getAvailableFieldsAsync(queryBuilder.dataSource);
        // Add source prefix to field IDs for global storage
        const processedFields = fields.map(field => ({
          ...field,
          id: `${queryBuilder.dataSource}_${field.id}`, // Prefix field ID with source
          label: field.label, // Keep original label for display
          source: queryBuilder.dataSource // Track which source this field comes from
        }));

        if (mounted) setAvailableFields(processedFields);
      } catch (e) {
        // Fallback to static fields
        const staticFields = getAvailableFields(queryBuilder.dataSource);
        const processedFields = staticFields.map(field => ({
          ...field,
          id: `${queryBuilder.dataSource}_${field.id}`,
          label: field.label,
          source: queryBuilder.dataSource
        }));

        if (mounted) setAvailableFields(processedFields);
      }
    };

    load();
    return () => { mounted = false; };
  }, [queryBuilder.dataSource]);

  // Filter fields based on search term
  const filteredFields = useMemo(() => {
    if (!fieldSearchTerm.trim()) {
      return availableFields;
    }
    return availableFields.filter(field =>
      field.label.toLowerCase().includes(fieldSearchTerm.toLowerCase()) ||
      field.id.toLowerCase().includes(fieldSearchTerm.toLowerCase())
    );
  }, [availableFields, fieldSearchTerm]);

  const handleDataSourceChange = (dataSourceId) => {
    setQueryBuilder(prev => ({
      ...prev,
      dataSource: dataSourceId,
      department: '',
      status: ''
    }));
    setReportData([]);
  };

  const handleFieldToggle = (fieldId) => {
    setQueryBuilder(prev => ({
      ...prev,
      selectedFields: prev.selectedFields.includes(fieldId)
        ? prev.selectedFields.filter(id => id !== fieldId)
        : [...prev.selectedFields, fieldId]
    }));
  };

  const handleFieldReorder = (newOrder) => {
    setQueryBuilder(prev => ({
      ...prev,
      selectedFields: newOrder
    }));
  };

  const generateReport = async () => {
    if (!queryBuilder.dataSource || queryBuilder.selectedFields.length === 0) {
      toast.error('Please select a data source and at least one field');
      return;
    }

    setLoading(true);
    try {
      const result = await generateReportSmart(
        queryBuilder.dataSource,
        queryBuilder.selectedFields.map(fieldId => fieldId.replace(`${queryBuilder.dataSource}_`, '')),
        [],
        queryBuilder.sortBy,
        queryBuilder.sortOrder
      );

      const REPORT_ROW_CAP = 1000;
      setReportData(result.data);
      
      if (result.data.length >= REPORT_ROW_CAP) {
        toast.warning(`Report capped at ${REPORT_ROW_CAP.toLocaleString()} rows. The full dataset may contain more records — apply filters to narrow your results.`, { icon: '⚠️', duration: 6000 });
      } else if (result.data.length > 0) {
        toast.success(`Report generated with ${result.data.length} records.`);
      } else {
        toast.warning('No data found matching your criteria. Try adjusting your filters or data source.');
      }

    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(error.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    if (reportData.length === 0) {
      toast.error('No data to export. Please generate a report first.');
      return;
    }

    try {
      const filename = `report_${queryBuilder.dataSource}_${getTodayInSingaporeTimezone()}`;

      const metadata = {
        dataSource: queryBuilder.dataSource,
        dateRange: queryBuilder.dateRange,
        startDate: queryBuilder.startDate,
        endDate: queryBuilder.endDate,
        department: queryBuilder.department,
        status: queryBuilder.status,
        selectedFields: queryBuilder.selectedFields,
        generatedAt: new Date().toISOString(),
        recordCount: reportData.length
      };

      switch (format) {
        case 'csv':
          exportToCSV(reportData, filename, queryBuilder.selectedFields);
          break;
        case 'excel':
          await exportToExcel(reportData, filename, queryBuilder.selectedFields, availableFields, metadata);
          break;
        case 'pdf':
          exportToPDF(reportData, filename, queryBuilder.selectedFields, availableFields, metadata);
          break;
        default:
          toast.error('Unsupported export format');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Report Builder</h1>
          <p className="text-sm sm:text-lg text-gray-600 mt-1">Create custom reports from your HR data</p>
        </div>
        <div className="flex items-center gap-3">
          {reportData.length > 0 && (
            <>
              {reportData.length >= 1000 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Showing first 1,000 rows
                </span>
              )}
              <button
                onClick={() => exportReport('csv')}
                className="btn btn-admin-outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </button>
              <button
                onClick={() => exportReport('excel')}
                className="btn btn-admin-outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </button>
              <button
                onClick={() => exportReport('pdf')}
                className="btn btn-admin-outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-4">
        <div className="flex items-center flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Date Range</span>
            <select
              value={queryBuilder.dateRange || 'this_month'}
              onChange={(e) => {
                setQueryBuilder(prev => ({
                  ...prev,
                  dateRange: e.target.value,
                  startDate: '',
                  endDate: ''
                }));
              }}
              className="select-admin text-xs"
            >
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_quarter">This Quarter</option>
              <option value="last_quarter">Last Quarter</option>
              <option value="this_year">This Year</option>
              <option value="last_year">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {/* Custom Date Range Fields */}
            {queryBuilder.dateRange === 'custom' && (
              <>
                <span className="text-xs text-gray-500">From</span>
                <DatePickerField
                  selected={parseLocalDate(queryBuilder.startDate)}
                  onChange={(date) => setQueryBuilder(prev => ({ ...prev, startDate: date ? toLocalISODate(date) : '' }))}
                  className="input-admin text-xs pl-12 w-40"
                  wrapperClassName="w-40"
                  placeholderText="Start date"
                  isClearable
                  maxDate={parseLocalDate(queryBuilder.endDate)}
                />
                <span className="text-xs text-gray-500">To</span>
                <DatePickerField
                  selected={parseLocalDate(queryBuilder.endDate)}
                  onChange={(date) => setQueryBuilder(prev => ({ ...prev, endDate: date ? toLocalISODate(date) : '' }))}
                  className="input-admin text-xs pl-12 w-40"
                  wrapperClassName="w-40"
                  placeholderText="End date"
                  isClearable
                  minDate={parseLocalDate(queryBuilder.startDate)}
                />
              </>
            )}
          </div>

          <div className="flex-1" />

          <button
            onClick={generateReport}
            disabled={loading || !queryBuilder.dataSource || queryBuilder.selectedFields.length === 0}
            className="btn btn-admin-primary btn-sm"
          >
            {loading ? (<><div className="loading-spinner-admin-sm mr-2"></div>Loading...</>) : (<><FileText className="w-4 h-4 mr-2" />Generate Report</>)}
          </button>
        </div>
      </div>

      {/* Report Builder - 3 Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Panel - Data Source */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border p-6 flex flex-col h-full">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-base font-semibold text-gray-900 truncate min-w-0">Data Source</h3>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {queryBuilder.selectedFields.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">Fields:</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {queryBuilder.selectedFields.length}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3 flex-1">
                {dataSources.map((source) => {
                  const Icon = source.icon;
                  const isSelected = queryBuilder.dataSource === source.id;

                  return (
                    <div
                      key={source.id}
                      className={`w-full p-4 border rounded-lg transition relative cursor-pointer ${
                        isSelected
                          ? 'border-red-500 bg-red-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setQueryBuilder(prev => ({
                          ...prev,
                          dataSource: source.id
                        }));
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-red-100' : 'bg-gray-100'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            isSelected ? 'text-red-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{source.label}</h4>
                          <p className="text-xs text-gray-500">{source.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Available Fields Panel */}
          {queryBuilder.dataSource && (
            <div className="lg:col-span-1">
              <AvailableFieldsPanel
                availableFields={availableFields}
                selectedFields={queryBuilder.selectedFields}
                onFieldToggle={handleFieldToggle}
                searchTerm={fieldSearchTerm}
                onSearchChange={setFieldSearchTerm}
              />
            </div>
          )}

          {/* Selected Fields Panel */}
          {queryBuilder.dataSource && (
            <div className="lg:col-span-1">
              <SelectedFieldsPanel
                availableFields={availableFields}
                selectedFields={queryBuilder.selectedFields}
                onFieldToggle={handleFieldToggle}
                onReorderFields={handleFieldReorder}
              />
            </div>
          )}
        </div>
    </div>
  );
};

export default AdhocReports;